<template>
  <div :class="controlClasses" class="mds-form-control mds-suggestion-select">
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <input
          ref="searchInput"
          type="text"
          autocomplete="off"
          :disabled="disabled"
          :id="uid"
          :placeholder="placeholder"
          @input="onSearchValueInput"
          v-model="searchValue"
          @focusin="onSearchInputFocusIn"
          @focusout="onSearchInputFocusOut"
          @blur="onSearchInputBlur"
        />

        <button
          type="button"
          ref="clearButton"
          tabindex="-1"
          class="clear"
          @click="onClear"
          :class="{ visible: showClear }"
        >
          <VIcon material="cancel" />
        </button>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>

    <VPopup
      ref="selectPopup"
      class="mds-suggestion-popup-wrapper"
      @keydown="keyDownListeners"
      @update:visible="setFocusOut"
      :offset="1"
      :target="'#' + uid"
      :visible="isFocused"
      align="left"
      inherit-width
      no-pointer
      position="bottom"
    >
      <div class="mds-suggestion-popup">
        <div
          v-show="Utils.isNotEmptyArray(options) || hasChosenValue || Utils.isNotEmptyArray(nestedOptions)"
          class="mds-suggestion-select-options"
        >
          <div
            v-for="(option, key) of options"
            :key="key"
            class="mds-suggestion-select-options-item"
            :class="getOptionClasses(option)"
            @click.stop="onOptionClick(option)"
          >
            <template v-if="hasOptionSlot">
              <slot name="option" v-bind:option="option" />
            </template>
            <template v-else>
              <div v-html="option.htmlText"></div>
              <div v-if="getOptionDescription(option)" class="description">
                {{ getOptionDescription(option) }}
              </div>
            </template>
          </div>
        </div>

        <div
          v-show="options.length === 0"
          class="mds-suggestion-select-options-empty"
        >
          <template v-if="hasEmptyOptionsSlot">
            <!-- @slot Слот вспомогательного текста -->
            <slot name="empty-options" />
          </template>
          <template v-else>
            Ничего не найдено
          </template>
        </div>
      </div>
    </VPopup>
  </div>
</template>

<style lang="scss" src="./VSuggestionSelect.scss" />

<script lang="ts">
import debounce     from "lodash.debounce";
import Core         from "@/core/Core";
import Utils        from '@/core/utils/Utils';
import VBaseControl from "@/core/services/VBaseControl";

import VIcon  from "@/lib/components/VIcon/VIcon.vue";
import VPopup from "@/lib/components/VPopup/VPopup.vue";

export default defineComponent({
  name: "VSuggestionSelect",

  components: { VPopup, VIcon },

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model */
    modelValue: {
      type:    [Array, Object],
      default: null,
    },
    /** Имя свойства объекта, используемое в качестве лейбла */
    optionLabel: {
      type:    String,
      default: "name",
    },
    /** Имя свойства объекта-идентификатора */
    optionIdentity: {
      type:    String,
      default: "id",
    },
    /** Имя свойства объекта для подсказки (серым текстом под лейблом) */
    optionDescription: {
      type:    String,
      default: null,
    },
    /** Кнопка очистки */
    clearable: {
      type:    Boolean,
      default: true,
    },
    /** Endpoint для загрузки опций */
    dataUrl: {
      type:    String,
      default: null,
    },
    /** Имя параметра запроса с поисковой строкой */
    dataUrlParameterName: {
      type:    String,
      default: "pattern",
    },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => ["left", "center", "right"].includes(value),
    },
    /** Дополнительные опции, отображаемые первыми (перед результатами поиска) */
    nestedOptions: {
      type:    Array,
      default: () => [],
    },
  },

  emits: ['update:modelValue', 'blur', 'focus'],

  data() {
    return {
      Utils,
      isFocused: false,
      /** Значение, которое пользователь вводит или выбирает */
      chosenValue: {
        [this.optionIdentity]: null,
        [this.optionLabel]:    null,
        htmlText:              null,
      },
      /** Сущности, полученные с бэка по вхождению */
      loadedOptions: [],
      /** Для отмены текущего запроса */
      searchSubscription: null,
      /** Значение, предложенное как автодополнение */
      supposedValue: null,
      /** Словарь отменённых вхождений — не реагировать автодополнением */
      cancelledSupposedValues: [],
      /** Выбор через стрелки задаёт hoveredOption программно */
      hoveredOption: null,
    };
  },

  created() {
    /**
     * При создании компонента нужно миновать сеттер chosenValue, тк сеттер
     * триггерит эмит наверх, а нам нужно этого избежать.
     */
    this.chosenValue = this.createSuggestionSelectItem(this.modelValue);
  },

  watch: {
    /** Отслеживаем изменения модели снаружи */
    modelValue(newVal, oldVal) {
      if (Core.utils.isEqual(newVal, oldVal) === true) return;
      this.searchValue = newVal;
    },
  },

  computed: {
    /** Какое-то значение было предложено как автодополнение */
    isOptionSupposed(): boolean {
      return this.supposedValue !== null && this.supposedValue !== undefined;
    },

    hasChosenValue(): boolean {
      /** trim() — чтобы при нащёлкивании пробелов не показывать пустую строку */
      return Utils.string.isString(this.chosenValue[this.optionLabel])
        && this.chosenValue[this.optionLabel].trim() !== "";
    },

    /** Значение поисковой строки */
    searchValue: {
      set(value) {
        // При возобновлении ввода нужно обнулять hoveredOption.
        this.hoveredOption = null;
        const _value = this.createSuggestionSelectItem(value);
        this.setChosenValue(_value);
      },
      get() {
        // Если есть выбранная стрелочками опция — показываем её в поисковой строке.
        if (this.hoveredOption !== null) {
          return this.hoveredOption[this.optionLabel];
        }
        return this.chosenValue[this.optionLabel];
      },
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      return result;
    },

    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    /** Поле лейбла пустое — значение пустое */
    hasValue(): boolean {
      return this.chosenValue[this.optionLabel] !== null
        && this.chosenValue[this.optionLabel] !== "";
    },

    isValueEmpty(): boolean {
      return this.hasValue === false;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      classes.push(`${this.$attrs.class}`);
      if (this.isFocused === true)  classes.push("focused");
      if (this.isValueEmpty)        classes.push("empty");
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.disabled === false
        && this.isValueEmpty === false;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    hasEmptyOptionsSlot(): boolean {
      return this.$slots["empty-options"] !== undefined;
    },

    /** Варианты для показа в дропдауне */
    options() {
      if (this.hasChosenValue === true && this.isOptionSupposed === false) {
        // Только уникальные.
        return this.getUniqueObjectsById(
          this.nestedOptions.concat([this.chosenValue], this.loadedOptions)
        );
      }

      if (Utils.isNotEmptyArray(this.nestedOptions)) {
        // Только уникальные.
        return this.getUniqueObjectsById(
          this.nestedOptions.concat(this.loadedOptions)
        );
      }

      return this.loadedOptions;
    },

    hasOptionSlot(): boolean {
      return this.$slots.option !== undefined;
    },
  },

  methods: {
    /** Устанавливаем выбранное значение и эмитим v-model */
    setChosenValue(value) {
      if (Core.utils.isEqual(this.chosenValue, value) === true) return;
      if (value) this.chosenValue = value;
      this.$emit("update:modelValue", this.chosenValue);
    },

    onSearchValueInput: debounce(function () {
      this.search(this.searchValue);
    }, 300),

    onSearchInputFocusIn() {
      this.setFocusIn();
    },

    onSearchInputFocusOut() {
      this.setFocusOut();
    },

    async onSearchInputBlur() {
      await this.validate();
    },

    setFocusOut() {
      this.isFocused = false;
      window.removeEventListener("keydown", this.keyDownListeners);
      const searchInput = this.$refs.searchInput;
      if (searchInput && typeof searchInput.blur === "function") searchInput.blur();
      this.$emit("blur", this.modelValue);
    },

    setFocusIn() {
      this.validation.markTouched();

      // Если на момент фокуса есть какое-то значение — ищем по нему.
      if (this.searchValue) this.search(this.searchValue, false);

      setTimeout(() => {
        this.isFocused = true;
        this.$emit("focus");
        window.addEventListener("keydown", this.keyDownListeners);
      });
    },

    /** Поиск по вхождению */
    async search(query, needToAutoFinish = true) {
      if (this.dataUrl === null) {
        return;
      }
      if (query === "") {
        this.loadedOptions = [];
        return;
      }

      const params = { [this.dataUrlParameterName]: query };

      // Отмену предыдущего запроса делает RpcService по имени метода.
      const response = await this.$core.rpc.makeRequest(
        this.dataUrl,
        params,
        null,
        null,
        false,
        null,
        null,
        false,
        { abortPrevious: true },
      );

      if (response.isCancelled === true) {
        return;
      }
      if (response.ok === false) {
        return;
      }

      const data = response.data;

      // Best practice — ограничить число вариантов 10, а не вываливать всё.
      this.loadedOptions = this.highlightMatches(
        data.length > 8 ? data.slice(0, 7) : data,
        query,
      );

      if (needToAutoFinish === true) {
        this.autoFinishSearchValue(query);
      }
    },

    createSuggestionSelectItem(value) {
      // Объект — присваиваем как есть.
      if (Utils.isObject(value)) {
        return {
          ...value,
          htmlText: value[this.optionLabel],
        };
      }
      // Строка или что-то ещё — превращаем в объект с id и name.
      return {
        [this.optionIdentity]: null,
        [this.optionLabel]:    value,
        htmlText:              value,
      };
    },

    getOptionClasses(option) {
      const result: Record<string, boolean> = {};
      // Сравнение по ссылке, потому что id может и не быть.
      if (this.hoveredOption === option) result.hovered = true;
      if (this.chosenValue === option)   result.selected = true;
      return result;
    },

    onOptionClick(option) {
      this.searchValue = option;
    },

    getOptionDescription(option) {
      if (this.optionDescription === null) return null;
      return option[this.optionDescription];
    },

    autoFinishSearchValue(searchTerm) {
      /**
       * Для автодополнения нужен массив вариантов и длина <= 4 — 4 оставшихся
       * варианта хватит, чтобы пользователь "угадал" нужный.
       */
      if (
        Utils.isNotEmptyArray(this.options) === false
        || this.options.length > 4
        || searchTerm === ""
      ) {
        return;
      }

      /**
       * autoFinishSearchValue срабатывает всегда, в т.ч. при стирании
       * автоподставленного слова. Без проверки ниже был бы бесконечный
       * цикл подставлений.
       */
      if (this.isOptionSupposed === true) {
        this.supposedValue = null;
        /**
         * При побуквенном стирании автоподстановка не должна срабатывать
         * после каждой стёртой буквы. Поэтому ведём словарь исключений.
         */
        this.cancelledSupposedValues = this.generateSubwords(searchTerm);
        return;
      }

      // Текущее вхождение в чёрном списке — отменяем автоподставление.
      if (this.cancelledSupposedValues.includes(searchTerm) === true) return;

      const lowerCaseSearchTerm = searchTerm.toLowerCase();

      // Берём максимально подходящий вариант.
      this.supposedValue = this.findMostCompatibleItem(lowerCaseSearchTerm);

      if (this.supposedValue === null || this.supposedValue === undefined) return;

      this.searchValue = this.supposedValue[this.optionLabel];

      this.$nextTick(() => {
        // Выделяем ту часть фразы, которую подставили.
        this.$refs.searchInput.setSelectionRange(
          searchTerm.length,
          this.supposedValue[this.optionLabel].split("").length,
        );
        // Автодополнили опцию — красим её как hovered.
        this.hoveredOption = this.supposedValue;
      });
    },

    /** Поиск максимально подходящего варианта */
    findMostCompatibleItem(searchTerm) {
      /**
       * Главный критерий — совпадение начала строки. Цель: подставить опцию
       * в контрол так, чтобы курсор не "прыгнул" вправо.
       */
      return this.loadedOptions.find((option) =>
        option[this.optionLabel].toLowerCase().startsWith(searchTerm)
      );
    },

    keyDownListeners(event) {
      /**
       * Перенёс как есть из VSelect — назначение этого кода непонятно,
       * но вероятно для чего-то было.
       */
      if (event.target.tagName !== "INPUT" || event.key === "Enter") {
        event.preventDefault();
      }

      function isLastIndex(index, options) {
        return options.length - 1 === index;
      }

      function isFirstIndex(index) {
        return index === 0;
      }

      function isAvailableIndex(index, options) {
        return index > -1 && index <= options.length - 1;
      }

      function findHoveredElIndex(hoveredEl, options, optionIdentity) {
        return options.findIndex(
          (option) => option[optionIdentity] === hoveredEl[optionIdentity]
        );
      }

      switch (event.key) {
				/** Обрабатываем стрелку вниз */
        case "ArrowDown": {
          /** Если hovered элемента у нас нет */
          if (this.hoveredOption === null) {
            // Первая опция — дефолтная выбранная, её всегда игнорируем.
            this.hoveredOption = this.options[1];
            return;
          }

          /** Если у нас уже есть hovered элемент */
          /** Находим индекс */
          const index = findHoveredElIndex(this.hoveredOption, this.options, this.optionIdentity);
          if (isAvailableIndex(index, this.options) === false) return;

          /** Проверка не является ли hovered элемент последним в списке */
          if (isLastIndex(index, this.options) === false) {
            this.hoveredOption = this.options[index + 1];

            /** Если hovered элемент последний в списке, то зацикливаем, и он становится первым */
          } else if (isLastIndex(index, this.options)) {
            this.hoveredOption = this.options[0];
          }
          break;
        }
        case "ArrowUp": {
          // При ArrowUp каретка убегает в начало строки — предотвращаем.
          event.preventDefault();

          if (this.hoveredOption === null) {
            this.hoveredOption = this.options.at(-1);
            return;
          }

          /** Если у нас уже есть hovered элемент */
          /** Находим индекс */
          const index = findHoveredElIndex(this.hoveredOption, this.options, this.optionIdentity);
          if (isAvailableIndex(index, this.options) === false) return;

          /** Проверка не является ли hovered элемент первым в списке */
          if (isFirstIndex(index) === false) {
            this.hoveredOption = this.options[index - 1];

            /** Если hovered элемент первый в списке, то зацикливаем, и он становится последним */
          } else if (isFirstIndex(index)) {
            this.hoveredOption = this.options.at(-1);
          }
          break;
        }
        case "Enter":
          // Выбор стрелками + Enter — установить как выбранное.
          if (this.hoveredOption !== null) {
            this.setChosenValue(this.hoveredOption);
          } else {
            this.setChosenValue(this.supposedValue);
          }
          this.setFocusOut();
          break;
        case "Escape":
          this.setFocusOut();
          break;
      }
    },

    /** Выделяем вхождение среди вернувшегося массива */
    highlightMatches(data, searchTerm) {
			// Приводим searchTerm к нижнему регистру для нечувствительного поиска
      const lowerCaseSearchTerm = searchTerm.toLowerCase().trim();

      return data.map((item) => {
				// Проверяем, содержит ли поле name искомое значение
        const index = item.name.toLowerCase().indexOf(lowerCaseSearchTerm);
        const _item = {
          ...item,
          htmlText: item[this.optionLabel],
        };

				// Если вхождение найдено, обрабатываем строку
        if (index !== -1) {
					/** Здесь разделяем и властвуем */
          const beforeMatch = item.name.slice(0, index);
          const match       = item.name.slice(index, index + searchTerm.length);
          const afterMatch  = item.name.slice(index + searchTerm.length);

					/** Почему именно match выделяем? потому что наш поиск возвращает
					 * даже те варианты, где вхождение находится в середине или в конце строки,
					 * потому нам не получится сделать как в гугле, потому что
					 * у гугла все опшены начинаются с вхождения, и там можно выделять весь afterMatch,
					 * у нас бы такое смотрелось бы глупо */
          _item.htmlText = `${beforeMatch}<strong>${match}</strong>${afterMatch}`;
        }

        return _item;
      });
    },

    /** "Фонарь" → ["Фонарь", "Фонар", "Фона", "Фон", "Фо", "Ф"] */
    generateSubwords(word) {
      const subwords = [];
      for (let i = word.length; i > 0; i--) {
        subwords.push(word.slice(0, i));
      }
      return subwords;
    },

    onClear() {
      this.$emit("update:modelValue", null);
      this.$emit("blur", null);
    },

    getUniqueObjectsById(arr) {
      const uniqueIds = new Set();
      return arr.filter((item) => {
        if (uniqueIds.has(item.id) === false) {
          uniqueIds.add(item.id);
          return true;
        }
        return false;
      });
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.modelValue);
    },
  },
});
</script>
